<template>
  <div ref="wrap" class="cityscene" :class="{ 'cityscene--ready': ready }">
    <canvas
      ref="canvas"
      class="cityscene__canvas"
      :class="{ 'cityscene__canvas--grabbing': dragging }"
      @pointerdown="onDown"
      @pointermove="onPointerMove"
      @pointerup="onUp"
      @pointercancel="onCancel"
      @wheel.prevent="onWheel"
      @contextmenu.prevent
    />
    <!--
      Crisp HTML over the 3D, not text baked into the scene: the numbers are
      the point of the board and they have to be readable at any size. The
      camera never moves, so these are placed once per change.
    -->
    <div class="cityscene__labels">
      <!--
        The label IS the building: what it is, what a shift there brings back,
        and a dot for every place someone can stand - filled once somebody
        does. Flat, crisp at any zoom, and the thing you tap.
      -->
      <button
        v-for="l in buildingLabels"
        :key="l.id"
        class="citylabel citylabel--building"
        :class="{ 'citylabel--on': l.ready, 'citylabel--burned': l.burned }"
        :style="{ left: `${l.x}px`, top: `${l.y}px` }"
        @click.stop="$emit('pick', { kind: 'building', id: l.id })"
      >
        <span class="citylabel__name">{{ l.burned ? 'burned - tap to repair' : l.name }}</span>
        <span class="citylabel__row">
          <template v-if="l.pay"><Pip :kind="l.pay.kind" :n="-l.pay.n" signed /><span class="city-label__arrow" aria-hidden="true">→</span></template>
          <Pip v-if="l.pip" :kind="l.pip.kind" :n="l.pip.n" signed />
          <span v-if="l.slots" class="citylabel__slots">
            <span
              v-for="n in l.slots"
              :key="n"
              class="slotdot"
              :class="{ 'slotdot--filled': n <= l.staffed }"
            />
          </span>
        </span>
      </button>

      <button
        v-for="l in annexLabels"
        :key="l.id"
        class="citylabel citylabel--annex"
        :class="{ 'citylabel--on': l.ready }"
        :style="{ left: `${l.x}px`, top: `${l.y}px` }"
        :title="l.why"
        @click.stop="$emit('pick', { kind: 'annex', q: l.q, r: l.r })"
      >
        <span class="citylabel__name">Take it in</span>
        <span class="citylabel__row"><ActionCost :cost="l.cost" as-shifts /></span>
        <span class="citylabel__why">{{ l.why }}</span>
      </button>

      <span
        v-for="f in flashes"
        :key="f.key"
        class="cityflash"
        :style="{ left: `${f.x}px`, top: `${f.y}px` }"
        aria-hidden="true"
      >{{ f.text }}</span>
    </div>
    <!-- North, turning with the city (playtest 13): tap it and the city turns north-up. -->
    <button v-if="ready" type="button" class="map-north map-north--phone map-north--tap map-north--city" :style="{ transform: `rotate(${needle}rad)` }" aria-label="North - tap to turn the city north-up" @click.stop="tapNorth">
      <span class="map-north__needle" aria-hidden="true" /><span class="map-north__n">N</span>
    </button>
    <button v-if="moved" class="cityscene__reset" @click="reset">Reset view</button>
    <p v-if="!ready" class="cityscene__loading">Building the city…</p>
  </div>
</template>

<script>
import ActionCost from './ActionCost.vue';
import Pip from './Pip.vue';
import { loadLazy } from '@/utils/preloadRecovery';

// The Stewards' city as the table sees the island: the same Kenney models, a
// fixed isometric camera, zoomed in far enough to read a building. three.js
// loads only when this board is on screen.
export default {
  name: 'CityScene',
  components: { ActionCost, Pip },
  props: {
    city: { type: Object, required: true },
    // The labels to hang over the scene. An ARRAY, not a function: a
    // function prop is not reactive, so a label that appeared because you
    // picked something never showed up until the camera happened to move.
    labels: { type: Array, default: () => [] },
    // { id, text } - shown rising off a building once, when something happened.
    flash: { type: Object, default: null },
    // Pull the camera back to take in the land around the city.
    wide: { type: Boolean, default: false },
  },
  emits: ['pick'],
  data() {
    return {
      ready: false, placed: [], dragging: false, moved: false, flashes: [],
      // The camera's azimuth, for the needle (north is up at PI/2).
      azimuth: Math.PI / 2,
    };
  },
  computed: {
    needle() { return -(this.azimuth - Math.PI / 2); },
    buildingLabels() { return this.placed.filter((l) => l.kind === 'building'); },
    annexLabels() { return this.placed.filter((l) => l.kind === 'annex'); },
  },
  watch: {
    city: {
      handler() { this.draw(); },
      deep: true,
    },
    wide(on) { if (this.world) this.world.setWide(on); },
    labels() { this.place(); },
    flash(f) {
      if (!f || !this.world) return;
      const at = this.world.screenOf(f.id);
      if (!at) return;
      const key = `${f.id}-${Date.now()}`;
      this.flashes.push({ key, text: f.text, ...at });
      window.setTimeout(() => { this.flashes = this.flashes.filter((x) => x.key !== key); }, 1100);
    },
  },
  created() {
    this.pointers = new Map();
    this.down = null;
    this.pinch = 0;
  },
  async mounted() {
    const loaded = await loadLazy(
      () => import('@/world/cityWorld'),
      () => import('@/utils/eventTheatre'),
    );
    // null: a lazy file failed and the page is already reloading.
    if (!loaded || !this.$refs.canvas) return;
    const [{ createCityWorld }, { prefersReducedMotion }] = loaded;
    this.world = createCityWorld(this.$refs.canvas, { reducedMotion: prefersReducedMotion() });
    this.world.setOnMove(() => { this.azimuth = this.world.azimuthOf?.() ?? this.azimuth; this.place(); });
    this.azimuth = this.world.azimuthOf?.() ?? this.azimuth;
    // Dev only: lets the screenshot harness ask what is under a point, the
    // same way window.__preview lets it drive a game.
    if (import.meta.env.DEV) window.__city = this.world;
    this.observer = new ResizeObserver(() => this.fit());
    this.observer.observe(this.$refs.wrap);
    this.fit();
    await this.world.ready;
    this.ready = true;
    this.draw();
  },
  beforeUnmount() {
    if (this.observer) this.observer.disconnect();
    if (this.world) this.world.dispose();
  },
  methods: {
    tapNorth() { if (this.world?.toggleNorth) { this.world.toggleNorth(); this.moved = true; } },
    fit() {
      const el = this.$refs.wrap;
      if (!el || !this.world) return;
      this.world.resize(el.clientWidth, el.clientHeight);
      this.place();
    },
    draw() {
      if (!this.world || !this.ready) return;
      this.world.sync(this.city);
      this.$nextTick(() => this.place());
    },
    place() {
      if (!this.world || !this.ready) return;
      this.placed = this.labels
        .map((l) => {
          const at = this.world.screenOf(l.id);
          if (!at) return null;
          return { ...l, ...at };
        })
        .filter((l) => l && Number.isFinite(l.x));
    },
    // A drag is never a tap (8px), the way the table's world behaves.
    onDown(e) {
      if (!this.world) return;
      this.$refs.canvas.setPointerCapture?.(e.pointerId);
      this.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (this.pointers.size === 1) this.down = { x: e.clientX, y: e.clientY, moved: 0 };
      if (this.pointers.size === 2) this.pinch = this.spread();
    },
    onPointerMove(e) {
      if (!this.world || !this.pointers.has(e.pointerId)) return;
      const last = this.pointers.get(e.pointerId);
      const dx = e.clientX - last.x;
      const dy = e.clientY - last.y;
      this.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (this.pointers.size >= 2) {
        const now = this.spread();
        if (this.pinch > 0 && now > 0) this.world.zoomBy(now / this.pinch);
        this.pinch = now;
        this.moved = true;
        return;
      }
      if (this.down) {
        this.down.moved += Math.abs(dx) + Math.abs(dy);
        if (this.down.moved > 8) { this.dragging = true; this.moved = true; }
      }
      if (this.dragging) this.world.orbit(dx);
    },
    onUp(e) {
      const wasTap = this.pointers.size === 1 && this.down && this.down.moved <= 8;
      this.pointers.delete(e.pointerId);
      if (this.pointers.size < 2) this.pinch = 0;
      if (wasTap) this.tapAt(e);
      if (!this.pointers.size) { this.dragging = false; this.down = null; }
    },
    onCancel(e) {
      this.pointers.delete(e.pointerId);
      if (!this.pointers.size) { this.dragging = false; this.down = null; this.pinch = 0; }
    },
    onWheel(e) {
      if (this.world) { this.world.zoomBy(e.deltaY < 0 ? 1.12 : 1 / 1.12); this.moved = true; }
    },
    spread() {
      const [a, b] = [...this.pointers.values()];
      return a && b ? Math.hypot(a.x - b.x, a.y - b.y) : 0;
    },
    reset() {
      if (this.world) this.world.resetView();
      this.moved = false;
    },
    tapAt(e) {
      if (!this.world) return;
      const r = this.$refs.canvas.getBoundingClientRect();
      const nx = ((e.clientX - r.left) / r.width) * 2 - 1;
      const ny = -(((e.clientY - r.top) / r.height) * 2 - 1);
      const hit = this.world.pick(nx, ny);
      if (hit) this.$emit('pick', hit);
    },
  },
};
</script>
